import {
  getMeetingIdFromMeetingNoteSearchId,
  getMeetingIdFromMeetingSummarySearchId,
  type SearchItem,
} from '@littlebird/sdk';

export type PresentedSearchCmdBarResult =
  | {
      id: string;
      type: 'thread';
      title: string;
      keywords: string;
      updatedAt: string;
      mentionCount: number;
      reportTemplateId?: number;
    }
  | {
      id: string;
      type: 'message';
      threadId: string;
      title: string;
      content: string;
      matchedLine: string;
      keywords: string;
      updatedAt: string;
      reportTemplateId?: number;
    }
  | {
      id: string;
      type: 'journal';
      title: string;
      content: string;
      keywords: string;
      updatedAt: string;
    }
  | {
      id: string;
      type: 'meeting_summary';
      meetingId: string;
      title: string;
      content: string;
      keywords: string;
      updatedAt: string;
    }
  | {
      id: string;
      type: 'meeting_note';
      meetingId: string;
      title: string;
      content: string;
      keywords: string;
      updatedAt: string;
    };

interface PresentSearchCmdBarResultsInput {
  searchResults: SearchItem[];
  query: string;
  getThreadTitle: (threadId: string) => string | undefined;
  getRoutineTitle: (reportTemplateId: number) => string | undefined;
}

const MATCHED_LINE_MAX_LENGTH = 120;

function getMatchedLine(content: string, query: string): string {
  const needle = query.trim().toLowerCase();
  const lines = content.split('\n');
  const line =
    (needle && lines.find((l) => l.toLowerCase().includes(needle))) ||
    lines.find((l) => l.trim()) ||
    '';
  const trimmed = line.trim();
  if (trimmed.length <= MATCHED_LINE_MAX_LENGTH) return trimmed;

  const index = needle ? trimmed.toLowerCase().indexOf(needle) : -1;
  const center = index < 0 ? 0 : index + Math.floor(needle.length / 2);
  const start = Math.max(
    0,
    Math.min(
      center - MATCHED_LINE_MAX_LENGTH / 2,
      trimmed.length - MATCHED_LINE_MAX_LENGTH
    )
  );
  return trimmed.slice(start, start + MATCHED_LINE_MAX_LENGTH);
}

export function presentSearchCmdBarResults({
  searchResults,
  query,
  getThreadTitle,
  getRoutineTitle,
}: PresentSearchCmdBarResultsInput): PresentedSearchCmdBarResult[] {
  const mentionCounts = new Map<string, number>();
  for (const item of searchResults) {
    if (item.type === 'message') {
      mentionCounts.set(
        item.threadId,
        (mentionCounts.get(item.threadId) ?? 0) + 1
      );
    }
  }

  const routineTitleOf = (reportTemplateId: number | undefined) =>
    reportTemplateId === undefined
      ? undefined
      : getRoutineTitle(reportTemplateId)?.trim();

  return searchResults.reduce<PresentedSearchCmdBarResult[]>(
    (results, item) => {
      if (item.type === 'thread') {
        const title =
          routineTitleOf(item.reportTemplateId) || (item.title?.trim() ?? '');
        if (title) {
          results.push({
            id: item.id,
            type: 'thread',
            title,
            keywords: title,
            updatedAt: item.updatedAt,
            mentionCount: mentionCounts.get(item.id) ?? 0,
            reportTemplateId: item.reportTemplateId,
          });
        }
        return results;
      }

      if (item.type === 'message') {
        const content = item.content ?? '';
        results.push({
          id: item.id,
          type: 'message',
          threadId: item.threadId,
          title:
            routineTitleOf(item.reportTemplateId) ||
            getThreadTitle(item.threadId) ||
            'Untitled',
          content,
          matchedLine: getMatchedLine(content, query),
          keywords: content,
          updatedAt: item.updatedAt,
          reportTemplateId: item.reportTemplateId,
        });
        return results;
      }

      if (item.type === 'journal') {
        const title = item.title || 'Untitled';
        const content = item.content ?? '';
        results.push({
          id: item.id,
          type: 'journal',
          title,
          content,
          keywords: [title, content].filter(Boolean).join(' '),
          updatedAt: item.updatedAt,
        });
        return results;
      }

      if (item.type === 'meeting_summary') {
        if (!item.content) return results;

        const meetingId = getMeetingIdFromMeetingSummarySearchId(item.id);
        if (!meetingId) return results;

        const title = item.title || 'Untitled';
        results.push({
          id: item.id,
          type: 'meeting_summary',
          meetingId,
          title,
          content: item.content,
          keywords: [title, item.content].filter(Boolean).join(' '),
          updatedAt: item.updatedAt,
        });
        return results;
      }

      if (item.type === 'meeting_note') {
        const meetingId = getMeetingIdFromMeetingNoteSearchId(item.id);
        if (!meetingId || !item.content) return results;

        const title = item.title || 'Untitled';
        results.push({
          id: item.id,
          type: 'meeting_note',
          meetingId,
          title,
          content: item.content,
          keywords: [title, item.content].filter(Boolean).join(' '),
          updatedAt: item.updatedAt,
        });
      }
      return results;
    },
    []
  );
}

export function getSearchActionSubtitle(content: string, query: string) {
  if (!query)
    return (
      <span className="text-ellipsis text-foreground-muted text-xs">
        {content.slice(0, 100)}
      </span>
    );

  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(escaped, 'i');
  const match = content.match(regex);

  if (!match)
    return (
      <span className="text-ellipsis text-foreground-muted text-xs">
        {content.slice(0, 100)}
      </span>
    );

  const index = match.index ?? content.indexOf(match[0]);
  const matchText = match[0];
  const left = `...${content.slice(Math.max(0, index - 30), index)} `;
  const right = ` ${content.slice(
    index + matchText.length,
    index + matchText.length + 30
  )}...`;

  return (
    <span className="text-foreground-muted text-ellipsis text-xs">
      {left}
      <mark className="text-accent bg-transparent">{match}</mark>
      {right}
    </span>
  );
}
